/**
 * Messaging API Service
 *
 * Provides API methods for the direct messaging feature including:
 * - Fetching conversations and messages
 * - Sending, editing, deleting messages
 * - Conversation management (archive, mute, read receipts)
 * - Message search
 */

import { api } from './client';
import type { ApiResponse } from '@/types';
import type {
  Conversation,
  Message,
  GetConversationsParams,
  GetConversationsResponse,
  GetMessagesParams,
  GetMessagesResponse,
  StartConversationResponse,
  SendMessageResponse,
  EditMessageResponse,
  UnreadCountResponse,
  SearchMessagesParams,
  SearchMessagesResponse,
  CreateGroupRequest,
  CreateGroupResponse,
  UpdateGroupRequest,
  AddMembersRequest,
  ConvertToGroupResponse,
} from '@/types/messaging';

const MESSAGES_BASE_URL = '/messages';

export const messagesApi = {
  // ============================================================================
  // Conversation Endpoints
  // ============================================================================

  /**
   * Get user's conversations with pagination
   */
  async getConversations(
    params: GetConversationsParams = {}
  ): Promise<ApiResponse<GetConversationsResponse>> {
    const { limit = 50, skip = 0, includeArchived = false } = params;

    const response = await api.get<GetConversationsResponse>(
      `${MESSAGES_BASE_URL}/conversations`,
      { params: { limit, skip, includeArchived } }
    );

    return response;
  },

  /**
   * Get a specific conversation by ID
   */
  async getConversation(conversationId: string): Promise<ApiResponse<Conversation>> {

    const response = await api.get<Conversation>(
      `${MESSAGES_BASE_URL}/conversations/${conversationId}`
    );

    return response;
  },

  /**
   * Start a new conversation or get existing one with initial message
   */
  async startConversation(
    recipientId: string,
    message: string
  ): Promise<ApiResponse<StartConversationResponse>> {

    const response = await api.post<StartConversationResponse>(
      `${MESSAGES_BASE_URL}/conversations`,
      { recipientId, message }
    );

    return response;
  },

  // ============================================================================
  // Message Endpoints
  // ============================================================================

  /**
   * Get messages in a conversation with cursor-based pagination
   */
  async getMessages(
    conversationId: string,
    params: GetMessagesParams = {}
  ): Promise<ApiResponse<GetMessagesResponse>> {
    const { limit = 50, before } = params;

    const queryParams: Record<string, string | number> = { limit };
    if (before) {
      queryParams.before = before;
    }

    const response = await api.get<GetMessagesResponse>(
      `${MESSAGES_BASE_URL}/conversations/${conversationId}/messages`,
      { params: queryParams }
    );

    return response;
  },

  /**
   * Send a message in a conversation
   */
  async sendMessage(
    conversationId: string,
    content: string,
    replyToId?: string
  ): Promise<ApiResponse<SendMessageResponse>> {

    const body: { content: string; replyToId?: string } = { content };
    if (replyToId) {
      body.replyToId = replyToId;
    }

    const response = await api.post<SendMessageResponse>(
      `${MESSAGES_BASE_URL}/conversations/${conversationId}/messages`,
      body
    );

    return response;
  },

  /**
   * Edit a message (within 15 minute window)
   */
  async editMessage(
    messageId: string,
    content: string
  ): Promise<ApiResponse<EditMessageResponse>> {

    const response = await api.put<EditMessageResponse>(
      `${MESSAGES_BASE_URL}/${messageId}`,
      { content }
    );

    return response;
  },

  /**
   * Soft delete a message
   */
  async deleteMessage(messageId: string): Promise<ApiResponse<void>> {

    const response = await api.delete<void>(`${MESSAGES_BASE_URL}/${messageId}`);

    return response;
  },

  // ============================================================================
  // Conversation Management Endpoints
  // ============================================================================

  /**
   * Mark a conversation as read
   */
  async markAsRead(conversationId: string): Promise<ApiResponse<void>> {

    const response = await api.post<void>(
      `${MESSAGES_BASE_URL}/conversations/${conversationId}/read`
    );

    return response;
  },

  /**
   * Archive a conversation
   */
  async archiveConversation(conversationId: string): Promise<ApiResponse<void>> {

    const response = await api.post<void>(
      `${MESSAGES_BASE_URL}/conversations/${conversationId}/archive`
    );

    return response;
  },

  /**
   * Unarchive a conversation
   */
  async unarchiveConversation(conversationId: string): Promise<ApiResponse<void>> {

    const response = await api.post<void>(
      `${MESSAGES_BASE_URL}/conversations/${conversationId}/unarchive`
    );

    return response;
  },

  /**
   * Mute a conversation until specified time
   */
  async muteConversation(
    conversationId: string,
    mutedUntil: Date
  ): Promise<ApiResponse<void>> {

    const response = await api.post<void>(
      `${MESSAGES_BASE_URL}/conversations/${conversationId}/mute`,
      { mutedUntil: mutedUntil.toISOString() }
    );

    return response;
  },

  /**
   * Unmute a conversation
   */
  async unmuteConversation(conversationId: string): Promise<ApiResponse<void>> {

    const response = await api.post<void>(
      `${MESSAGES_BASE_URL}/conversations/${conversationId}/unmute`
    );

    return response;
  },

  // ============================================================================
  // Group Conversation Endpoints
  // ============================================================================

  /**
   * Create a new group conversation
   */
  async createGroup(data: CreateGroupRequest): Promise<ApiResponse<CreateGroupResponse>> {

    const response = await api.post<CreateGroupResponse>(
      `${MESSAGES_BASE_URL}/conversations/groups`,
      data
    );

    return response;
  },

  /**
   * Update group name and/or avatar
   */
  async updateGroup(
    conversationId: string,
    updates: UpdateGroupRequest
  ): Promise<ApiResponse<void>> {

    const response = await api.patch<void>(
      `${MESSAGES_BASE_URL}/conversations/${conversationId}/group`,
      updates
    );

    return response;
  },

  /**
   * Add members to a group
   */
  async addMembers(
    conversationId: string,
    data: AddMembersRequest
  ): Promise<ApiResponse<void>> {

    const response = await api.post<void>(
      `${MESSAGES_BASE_URL}/conversations/${conversationId}/members`,
      data
    );

    return response;
  },

  /**
   * Remove a member from a group
   */
  async removeMember(
    conversationId: string,
    memberId: string
  ): Promise<ApiResponse<void>> {

    const response = await api.delete<void>(
      `${MESSAGES_BASE_URL}/conversations/${conversationId}/members/${memberId}`
    );

    return response;
  },

  /**
   * Leave a group conversation
   */
  async leaveGroup(conversationId: string): Promise<ApiResponse<void>> {

    const response = await api.post<void>(
      `${MESSAGES_BASE_URL}/conversations/${conversationId}/leave`
    );

    return response;
  },

  /**
   * Convert a 1:1 conversation to a group by adding new members
   * Existing participants keep prior history; newly added members only see
   * messages created after they join.
   */
  async convertToGroup(
    conversationId: string,
    memberIds: string[],
    groupName?: string
  ): Promise<ApiResponse<ConvertToGroupResponse>> {

    const response = await api.post<ConvertToGroupResponse>(
      `${MESSAGES_BASE_URL}/conversations/${conversationId}/convert-to-group`,
      { memberIds, groupName }
    );

    return response;
  },

  // ============================================================================
  // Utility Endpoints
  // ============================================================================

  /**
   * Get total unread message count across all conversations
   */
  async getUnreadCount(): Promise<ApiResponse<UnreadCountResponse>> {

    const response = await api.get<UnreadCountResponse>(
      `${MESSAGES_BASE_URL}/unread-count`
    );

    return response;
  },

  /**
   * Search messages across conversations
   */
  async searchMessages(
    query: string,
    params: SearchMessagesParams = {}
  ): Promise<ApiResponse<SearchMessagesResponse>> {
    const { conversationId, limit = 20, skip = 0 } = params;

    const queryParams: Record<string, string | number> = {
      q: query,
      limit,
      skip,
    };
    if (conversationId) {
      queryParams.conversationId = conversationId;
    }

    const response = await api.get<SearchMessagesResponse>(
      `${MESSAGES_BASE_URL}/search`,
      { params: queryParams }
    );

    return response;
  },
};
